import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
import { deleteFile } from '../../selectedVideoSlice';
import { RootState } from '../../../../reducers';
import { getFolderUrl } from '../../../../lib/routingUtils';
import { FilePreviewRouteParams } from '../../types';
import { deselectAllFiles } from '../../../library/librarySlice';

interface DeleteFilesModalProps {
  onClose?: () => void;
}

export const DeleteFilesModal = ({ onClose }: DeleteFilesModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teamId, folderId, fileId } = useParams<FilePreviewRouteParams>();
  const { globalModals } = useSelector((state: RootState) => state);
  const { files = [], status } = globalModals.actionData || {};
  const isLoading = status === 'pending';

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleDeleteFiles = async () => {
    const fileCount = files.length;
    const fileName = files[0]?.name;

    files.forEach((file) => {
      dispatch(deleteFile(file._id));
    });
    dispatch(deselectAllFiles());

    toast(
      fileCount === 1
        ? t('selectedVideo.actions.modals.deleteFile.successSingle', {
            fileName,
          })
        : t('selectedVideo.actions.modals.deleteFile.successMultiple', {
            count: fileCount,
          }),
      { duration: 2000 },
    );

    handleClose();
    // If fileId is present, it means we are in selected video view
    if (teamId && folderId && fileId) {
      navigate(getFolderUrl({ teamId, folderId }));
    }
  };

  const fileCount = files.length;
  const title =
    fileCount === 1
      ? t('selectedVideo.actions.modals.deleteFile.titleSingle', {
          fileName: files[0]?.name,
        })
      : t('selectedVideo.actions.modals.deleteFile.titleMultiple', {
          count: fileCount,
        });

  const getDeleteButtonLabel = () => {
    if (isLoading) {
      return fileCount === 1
        ? t('selectedVideo.actions.modals.deleteFile.deletingSingle')
        : t('selectedVideo.actions.modals.deleteFile.deletingMultiple');
    }
    return fileCount === 1
      ? t('selectedVideo.actions.modals.deleteFile.deleteSingle')
      : t('selectedVideo.actions.modals.deleteFile.deleteMultiple');
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="line-clamp-2 pr-8">{title}</DialogTitle>
        </DialogHeader>
        <Alert variant="destructive" className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {fileCount === 1
              ? t('selectedVideo.actions.modals.deleteFile.warningSingle')
              : t('selectedVideo.actions.modals.deleteFile.warningMultiple')}
          </AlertDescription>
        </Alert>
        <DialogDescription className="mt-3">
          {fileCount === 1
            ? t('selectedVideo.actions.modals.deleteFile.descriptionSingle')
            : t('selectedVideo.actions.modals.deleteFile.descriptionMultiple', {
                count: fileCount,
              })}
        </DialogDescription>
        <div className="flex justify-end gap-2 mt-6">
          <Button
            onClick={handleClose}
            variant="outline"
            disabled={isLoading}
            data-testid="cancel-button-delete-modal"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteFiles}
            variant="destructive"
            disabled={isLoading}
            data-testid="delete-button-delete-modal"
          >
            {getDeleteButtonLabel()}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteFilesModal;
